import h from 'hyperscript';
import drillThroughIcon from '@disy/cadenza-icons/drill-through.svg';

import { AlertDialog } from 'ui/dialog/alert/alert-dialog';
import { setupContextMenu } from 'ui/menu/menu';

import { AFTER_TABLE_CONFIGURATION, DATATABLE_CELL_RENDERED } from 'cadenza/ui/base-table/base-table-event-types';
import {
  getColumnIndexForBackend,
  getColumnIndexForFrontend,
  getRowDataOfRows,
  getNonPrimitiveValueType } from 'cadenza/ui/base-table/base-table';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { logger } from 'cadenza/utils/logging';
import * as DrillThroughApi from 'cadenza/api-client/drill-through-api';
import { escapeAndMarkHTML } from 'cadenza/utils/escape-and-mark';
import { on, unByKey } from 'cadenza/utils/event-util';
import { ResultTablePlugin } from 'cadenza/result-table/result-table-plugin';
import { openDrillThroughUrlDialog } from 'cadenza/result-table/drill-through/drill-through-url-dialog';
import { icon } from 'cadenza/utils/icon/icon';
import { getMediaMetaData } from 'cadenza/api-client/media-gallery-api';
import { cadenzaTopWindow } from 'cadenza/utils/iframe';
import {
  getRowsWithSelection,
  isMultiColumnSelection,
  isRowSelection,
  ResultTableSelectionChangeEvent
} from 'cadenza/result-table/selection/selection-result-table-plugin';
import { assert } from 'cadenza/utils/custom-error';
import { addIconBehindCellContent } from 'cadenza/result-table/result-table-utils';

import './drill-through-classic-result-table-plugin.css';
import i18n from './drill-through.properties';

const COMPONENT_NAME = 'd-drill-through-classic-result-table-plugin';
const DRILL_THROUGH_ICON_CLASS = `${COMPONENT_NAME}--icon`;

const DESTINATION_TYPE_SELECTOR = 'SELECTOR';
const DESTINATION_TYPE_CUSTOM_WEBAPP = 'CUSTOM_WEB_APPLICATION';
const DESTINATION_TYPE_URL = 'URL';
const DESTINATION_TYPE_MEDIA_GALLERY = 'MEDIA_GALLERY';

class DrillThroughClassicResultTablePlugin extends ResultTablePlugin {

  _columns;
  _drillThroughConfiguration;
  _removeCurrentDrillThroughMenu;
  _eventKeys;
  _mediaGallery;

  init () {
    this._eventKeys = [
      on(this.table, AFTER_TABLE_CONFIGURATION, () => this._attachDrillThroughCellRenderer())
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  configureWithConfigurationData (columnConfiguration) {
    this._columns = columnConfiguration.columns;
    this._drillThroughConfiguration = columnConfiguration.drillThroughConfiguration;

    const drillThroughAvailable = this._drillThroughConfiguration
      && this._drillThroughConfiguration.enabled === true
      && this._drillThroughConfiguration.drillThroughDestinations;

    if (!drillThroughAvailable) {
      return;
    }

    logger.debug('Enable drill through for current dataTable');
    this._eventKeys.push(
      on(
        this.table,
        ResultTableSelectionChangeEvent.TYPE,
        (event) => this._onSelectionChange(event))
    );
  }

  _attachDrillThroughCellRenderer () {
    this.table.addEventListener(DATATABLE_CELL_RENDERED, (event) => {
      const { td, rowData } = event.detail;

      // Render arrow button only if we have drill-through destinations for the column and this is not a specially typed
      // cell (Subtotal, Others, Collapsed, etc.)
      const drillThroughDestinations = this._getDrillThroughDestinations(td.dDatatableColumnIndex);
      if (!drillThroughDestinations
        || drillThroughDestinations.length === 0
        || getNonPrimitiveValueType(td, rowData)) {
        return;
      }

      td.setAttribute('role', 'button');
      td.setAttribute('aria-haspopup', 'menu');

      addIconBehindCellContent(
        td,
        icon(drillThroughIcon,
          {
            styleClass: DRILL_THROUGH_ICON_CLASS,
            size: 'xs'
          })
      );
    });
  }

  _onSelectionChange (event) {
    const selection = event.selection;

    if (!this._shouldOpenDrillThroughMenu(selection)) {
      return;
    }

    const rowsData = this._getSelectedRowsData(selection);
    const selectedColumnIndices = selection.values().next().value;

    assert(
      selectedColumnIndices.size === 1,
      'Drill-through requires selections with one column selected, only');

    const columnIndex = selectedColumnIndices.values().next().value;

    return this._createDrillThroughMenuItems(rowsData, columnIndex)
      .then(menuItems => {
        if (menuItems.length > 0) {
          const rowId = selection.keys().next().value;
          const dataTable = this.table.getDataTable();
          const row  = dataTable.row(`#${rowId}`);
          const rowIndex = row.index();
          const tdElement = dataTable.cell(rowIndex, columnIndex).node();

          this._removeCurrentDrillThroughMenu = setupContextMenu(tdElement, menuItems, {
            appendTo: document.body,
            initiallyOpen: true
          });
        }
      })
      .catch(error => {
        showErrorDialog();
        logger.error('Failed to setup drill through context menu', error);
      });
  }

  _getDrillThroughDestinations (columnIndex) {
    const attributeName = this._columns[getColumnIndexForBackend(columnIndex)]?.name;
    const drillThroughDestinations = this._drillThroughConfiguration?.drillThroughDestinations;
    if (!drillThroughDestinations
      || !drillThroughDestinations[attributeName]) {
      return [];
    }
    return drillThroughDestinations[attributeName];
  }

  /**
   * Create the context menu items for the drill through destinations
   *
   * @param {object[]} rowsData - Data from the selected rows as served from backend
   * @param {int} columnIndex - Column index in column definition space (including index column and
   *   invisible columns)
   * @return {Promise<MenuItem[]>} A promise for the menu items
   */
  _createDrillThroughMenuItems (rowsData, columnIndex) {
    const activeDestinations = this._getDrillThroughDestinations(columnIndex)
      .filter((drillThroughDestination) => (
        // only selector destinations can be opened for multiple values
        rowsData.length === 1
        || drillThroughDestination.type === DESTINATION_TYPE_SELECTOR));

    const mediaGalleryDestinations = activeDestinations
      .filter(it => it.type === DESTINATION_TYPE_MEDIA_GALLERY);

    return Promise.all(
      // for all media gallery destinations, get the metadata from API
      mediaGalleryDestinations
        .map(destination => getMediaMetaData(
          destination.dataProviderName,
          rowsData[0].values[getColumnIndexForBackend(columnIndex)])
          .then(mediaMetaData => (destination.mediaMetaData = mediaMetaData))))
      .then(() => activeDestinations
        .map((drillThroughDestination, destinationIndex) =>
          this._createMenuItemForDestination(drillThroughDestination, destinationIndex, rowsData)));
  }

  _createMenuItemForDestination (drillThroughDestination, destinationIndex, rowsData) {
    // media gallery destinations that are empty are disabled
    const isEmptyMediaLib = drillThroughDestination.type === DESTINATION_TYPE_MEDIA_GALLERY
      && drillThroughDestination.mediaMetaData.length === 0;

    return {
      label: () => this._determineLabelForDestination(
        drillThroughDestination,
        destinationIndex,
        rowsData),
      execute: () => this._doDrillThrough(drillThroughDestination, rowsData),
      disabled: isEmptyMediaLib
    };
  }

  _determineLabelForDestination (drillThroughDestination, destinationIndex, rowsData) {
    let label = drillThroughDestination.printName;
    if (!label) {
      return i18n('drillThrough.emptyPrintNameText', { count: destinationIndex + 1 });
    }
    this._columns.forEach((column, columnIndex) => {
      const attributePlaceHolder = '${' + String(column.name) + '}';
      const attributeValue = rowsData[0].values[getColumnIndexForFrontend(columnIndex)];
      label = label.replace(attributePlaceHolder, attributeValue);
    });
    return label;
  }

  _doDrillThrough (drillThroughDestination, rowsData) {
    const type = drillThroughDestination.type;
    switch (type) {
      case DESTINATION_TYPE_SELECTOR:
        this._drillThroughToSelector(drillThroughDestination.id, rowsData);
        break;
      case DESTINATION_TYPE_CUSTOM_WEBAPP:
      case DESTINATION_TYPE_URL:
        this._drillThroughToUrl(drillThroughDestination, rowsData[0]);
        break;
      case DESTINATION_TYPE_MEDIA_GALLERY:
        this._drillThroughToMediaGallery(drillThroughDestination);
        break;
      default:
        logger.debug(`Unknown drill-through destination type [${type}]. Drill-Through target will not be displayed.`);
    }
  }

  _drillThroughToUrl (drillThroughDestination, rowData) {
    let targetUrl = drillThroughDestination.targetUrl;
    this._columns.forEach((column, index) => {
      const attributePlaceHolder = '${' + String(column.name) + '}';
      const attributeValue = rowData.values[index];
      targetUrl = cadenzaUrl(targetUrl.replace(attributePlaceHolder, attributeValue));
    });
    const openUrl = (targetAttributeOrWindowName) => {
      setTimeout(() => {
        const windowOptions = drillThroughDestination.options;
        // eslint-disable-next-line no-restricted-syntax -- This is classic code that will go away when workbooks are split out of Cadenza.
        window.open(
          targetUrl,
          targetAttributeOrWindowName,
          'noopener' + (windowOptions ? (',' + windowOptions) : ''));
      }, 0);
    };
    switch (drillThroughDestination.targetFrame) {
      case 'CURRENT_FRAME': {
      // CURRENT_FRAME means the frame that contains the Cadenza top window
        if (cadenzaTopWindow === window) {
          openUrl('_self');
        } else {
        // There is only one layer of iframe nesting within Cadenza itself. So if the current
        // window is not the Cadenza top window, it must be the parent frame of the current one.
          openUrl('_parent');
        }
        break;
      }
      case 'CURRENT_WINDOW': {
        openUrl('_top');
        break;
      }
      case 'NEW_WINDOW': {
        openUrl('_blank');
        break;
      }
      case 'NAMED_WINDOW': {
        openUrl(drillThroughDestination.frameName);
        break;
      }
      case 'INTERNAL': {
        openUrl('_self');
        break;
      }
      case 'INTERNAL_POPUP': {
        openDrillThroughUrlDialog(targetUrl, drillThroughDestination.options);
        break;
      }
      default: {
        logger.error('No valid targetFrame found for drillThroughDestination: ', drillThroughDestination.targetFrame);
        showErrorDialog();
      }
    }
  }

  _drillThroughToSelector (drillThroughTargetId, selectedRowsData, warningsAccepted) {
    DrillThroughApi.drillThrough(drillThroughTargetId, selectedRowsData, warningsAccepted)
      .then(processingChainUrlDto => location.replace(cadenzaUrl(processingChainUrlDto.url)))
      .catch(error => {
        if (error.status === 409 /* CONFLICT */) {
          const originalRequest = {
            drillThroughTargetId,
            selectedRowsData
          };
          this._handleDrillThroughWarnings(JSON.parse(error.responseText), originalRequest);
          return;
        }
        showErrorDialog();
        logger.error('Error on drill through', error);
      });
  }

  _handleDrillThroughWarnings (drillThroughWarnings, originalRequest) {
    const messages = drillThroughWarnings.map(warning => warning.message);
    const drillThroughWarningsDialog = new AlertDialog(i18n('drillThrough.warnings.title'), {
      type: 'warning',
      cancel: true,
      message: h('div',
        h('ul', messages.map(message => h('li', escapeAndMarkHTML(message)))),
        h('p', i18n('drillThrough.warnings.text')))
    });
    drillThroughWarningsDialog.addEventListener('close', () => {
      const confirmed = drillThroughWarningsDialog.returnValue;
      if (confirmed) {
        this._drillThroughToSelector(
          originalRequest.drillThroughTargetId, originalRequest.rowsData, true);
      }
      drillThroughWarningsDialog.remove();
    });
    document.body.append(drillThroughWarningsDialog);
    drillThroughWarningsDialog.showModal();
  }

  _drillThroughToMediaGallery (mediaGalleryDestination) {
    import(/* webpackChunkName: "media-gallery" */ 'cadenza/media/media-gallery')
      .then(({ MediaGallery }) => {
        const title = mediaGalleryDestination.printName;
        const media = mediaGalleryDestination.mediaMetaData;
        if (!this._mediaGallery) {
          this._mediaGallery = new MediaGallery(title, media);
          // Don't use the mediaGallery.onclose property. This does not work on iPad.
          this._mediaGallery.addEventListener('close', () => this._mediaGallery.remove());
        } else {
          // The dialog is re-used although creating a new instance on every drill-through would be
          // simpler, because otherwise Freewall does not layout the images properly anymore, the
          // second time the gallery is opened
          this._mediaGallery.setContent(title, media);
        }
        document.body.append(this._mediaGallery);
        this._mediaGallery.showModal();
      })
      .catch(error => logger.error('Failed to show media gallery', error));
  }

  _shouldOpenDrillThroughMenu (selection) {
    return selection.size > 0 // Selection not empty
      && !isRowSelection(selection) // Cell is not index column
      && !isMultiColumnSelection(selection);
  }

  _getSelectedRowsData (selection) {
    const rowIds = getRowsWithSelection(selection);
    return getRowDataOfRows(this.table.getDataTable(), rowIds)
      .map(({ id, values }) => ({
        formatted: [], // no need to send formatted values back to the server for drill through
        id,
        values: values.slice(1) // to remove row index value
      }));
  }

}

function showErrorDialog () {
  const errorDialog = new AlertDialog(i18n('drillThrough.error.title'), {
    message: i18n('drillThrough.error.message'),
    type: 'error'
  });
  document.body.appendChild(errorDialog);
  errorDialog.addEventListener('close', () => errorDialog.remove());
  errorDialog.showModal();
}

customElements.define(COMPONENT_NAME, DrillThroughClassicResultTablePlugin);
